/**
 * The left half of the creator-roster theater: whatever is playing.
 *
 * Split out of `CreatorTeamSection` when that file passed 800 lines. The
 * important property is that this renders EXACTLY ONE pane for any input — the
 * caller passes a single decided `pane` rather than three independent
 * conditions, which is what previously let the theater render nothing at all
 * and leave a blank column beside a populated rail.
 *
 * Order of preference, decided by the caller: a live on-game creator, else a
 * clip of the title, else the title's own key art, else a plain panel that
 * still holds the layout.
 */

import React, { useState } from "react";
import { type RosterHighlight } from "@devour/client";
import { StreamInlinePlayer, type InlinePlayerSource } from "./StreamInlinePlayer";
import { type RosterMember, safeCssUrl } from "./rosterMember";
/*
 * The same embed rules the publisher hero's player uses. Imported rather than
 * restated: "is this a file, is this YouTube, what is its still" has one right
 * answer per URL, and two copies of it drift.
 */
import { isDirectMedia, youTubeId } from "./PublisherHeroWatch";

export type TheaterPane = "live" | "clip" | "poster" | "bare";

interface CreatorTheaterPaneProps {
    pane: TheaterPane;
    /** Set when `pane` is "live". */
    selected?: RosterMember;
    selectedSource?: InlinePlayerSource;
    /** Set when `pane` is "clip". */
    activeHighlight?: RosterHighlight;
    /** Set when `pane` is "poster". */
    fallbackPosterUrl?: string;
    fallbackVideoUrl?: string;
    /** Whether the roster has anyone on it — changes what the bare pane says. */
    hasMembers: boolean;
    onClosePlayer: () => void;
}

export function CreatorTheaterPane({
    pane, selected, selectedSource, activeHighlight,
    fallbackPosterUrl, fallbackVideoUrl, hasMembers, onClosePlayer,
}: CreatorTheaterPaneProps): React.ReactElement | null {
    /*
     * Declared before every early return below — a hook called conditionally
     * is a different hook on the next render and React will not have it.
     */
    const [posterPlaying, setPosterPlaying] = useState(false);

    /*
     * The clip pane. Only when nobody is live — the server sends highlights only
     * in that case, so this cannot compete with a live stream for the theater.
     *
     * An <iframe> rather than StreamInlinePlayer: that component takes a channel
     * and mounts a LIVE embed, and a clip is a different object with its own
     * embed URL. Muted and not autoplayed — the hero above already has a
     * trailer, and two things playing at once is worse than one click.
     *
     * Labelled CLIP, prominently. A recorded clip dressed as a live stream is
     * the one thing this section must never do: the whole claim of the roster is
     * that "live" means live.
     */
    if (pane === "clip" && activeHighlight) {
        return (
            <div className="creator-team__player">
                <div className="creator-team__player-stage">
                    <iframe
                        title={activeHighlight.title}
                        src={`${activeHighlight.embedUrl}&parent=${window.location.hostname}&autoplay=false`}
                        allowFullScreen
                        frameBorder="0"
                        scrolling="no"
                    />
                </div>
                <div className="creator-team__player-meta">
                    <span className="creator-team__player-who">
                        <b>{activeHighlight.creatorLabel}</b>
                        <span>{activeHighlight.title}</span>
                    </span>
                    <span className="creator-team__player-actions">
                        <span className="creator-team__clip-tag">Clip</span>
                        {activeHighlight.creatorDropSlug &&
                            <a
                                className="creator-team__player-link"
                                href={`/drop-in/${activeHighlight.creatorDropSlug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Open their Drop ↗
                            </a>
                        }
                    </span>
                </div>
            </div>
        );
    }

    if (pane === "live" && selected && selectedSource) {
        return (
            <div className="creator-team__player">
                {/* The stage owns the 16/9 box — the player fills it. */}
                <div className="creator-team__player-stage">
                    <StreamInlinePlayer
                        source={{...selectedSource, autoplay: true, muted: true, viewerInitiated: true}}
                    />
                </div>
                <div className="creator-team__player-meta">
                    <span className="creator-team__player-who">
                        <b>{selected.label}</b>
                        {selected.streamTitle && <span>{selected.streamTitle}</span>}
                    </span>
                    <span className="creator-team__player-actions">
                        {/* New tab, same as the cards — leaving in place would
                            close the player you are watching. */}
                        {selected.dropSlug &&
                            <a
                                className="creator-team__player-link"
                                href={`/drop-in/${selected.dropSlug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Open their Drop ↗
                            </a>
                        }
                        <button
                            type="button"
                            className="creator-team__player-close"
                            onClick={onClosePlayer}
                        >
                            Close player
                        </button>
                    </span>
                </div>
            </div>
        );
    }

    /*
     * The pane of last resort with art: the title's own key art.
     *
     * Nobody live and no clips is the ORDINARY state of a publisher Drop for
     * most of its life — the game is not out, so there is nothing to stream and
     * nothing to clip. Leaving the pane empty made the section look broken in
     * exactly the window it most needs to look deliberate. Key art with the
     * trailer as the play target is always the title's own footage, so the
     * layout holds from the day a Drop is created.
     */
    if (pane === "poster" && fallbackPosterUrl) {
        /* What we can do with the trailer: play it, embed it, or only link it. */
        const posterVideoKind: "file" | "youtube" | "external" | "none" = !fallbackVideoUrl
            ? "none"
            : isDirectMedia(fallbackVideoUrl)
                ? "file"
                : youTubeId(fallbackVideoUrl)
                    ? "youtube"
                    : "external";

        return (
            <div className="creator-team__player">
                <div className="creator-team__player-stage">
                    {/*
                      * PLAYS HERE. It used to be a link to YouTube.
                      *
                      * This pane exists because nobody is live — the visitor is
                      * on the page with nothing to watch, which is the worst
                      * possible moment to hand them to another site. The
                      * trailer is the one thing we have; playing it in the
                      * stage keeps them here, and the stage is already the
                      * right shape for it.
                      *
                      * Click-to-play rather than autoplay: an iframe that
                      * mounts on load costs bandwidth for someone who never
                      * pressed play, and this pane renders on every quiet
                      * roster.
                      */}
                    {posterVideoKind === "file" && posterPlaying
                        ? <video
                            className="creator-team__poster-video"
                            src={fallbackVideoUrl}
                            poster={fallbackPosterUrl}
                            controls
                            autoPlay
                            playsInline
                        />
                        : posterVideoKind === "youtube" && posterPlaying
                            ? <iframe
                                className="creator-team__poster-video"
                                src={`https://www.youtube.com/embed/${youTubeId(fallbackVideoUrl)}?autoplay=1&rel=0&modestbranding=1`}
                                title="Trailer"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                            : posterVideoKind === "file" || posterVideoKind === "youtube"
                                ? <button
                                    type="button"
                                    className="creator-team__poster creator-team__poster--play"
                                    onClick={() => setPosterPlaying(true)}
                                    style={{backgroundImage: safeCssUrl(fallbackPosterUrl)}}
                                    aria-label="Play the trailer"
                                >
                                    <span className="creator-team__poster-play" aria-hidden="true">▶</span>
                                </button>
                                /*
                                 * Twitch and Kick pages cannot be embedded from
                                 * here, so those keep the link-out — a link we
                                 * cannot avoid is better than a play button
                                 * that navigates.
                                 */
                                : fallbackVideoUrl
                                    ? <a
                                        className="creator-team__poster creator-team__poster--play"
                                        href={fallbackVideoUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{backgroundImage: safeCssUrl(fallbackPosterUrl)}}
                                        aria-label="Watch the trailer"
                                    >
                                        <span className="creator-team__poster-play" aria-hidden="true">▶</span>
                                    </a>
                                    : <span
                                        className="creator-team__poster"
                                        style={{backgroundImage: safeCssUrl(fallbackPosterUrl)}}
                                    />
                    }
                </div>
                {/*
                  * No caption. It read "Nobody is live right now…" directly
                  * above a footer that already says "0 of 5 live right now" —
                  * the same fact twice, three lines apart. The play glyph on the
                  * art says what the pane is for without narrating it.
                  */}
            </div>
        );
    }

    /*
     * No live creator, no clip, and no key art to fall back on — a roster with
     * no release behind it, before anyone streams. The stage still renders,
     * holding the layout, and says what the reader should do next instead of
     * going blank.
     */
    return (
        <div className="creator-team__player">
            <div className="creator-team__player-stage">
                <span className="creator-team__poster creator-team__poster--bare">
                    <span className="creator-team__poster-note">
                        {hasMembers ? "Nobody is live right now" : "No creators on this team yet"}
                    </span>
                </span>
            </div>
            <div className="creator-team__player-meta">
                <span className="creator-team__player-who">
                    <span>
                        {hasMembers
                            ? "Open a creator below to watch them next time they go live."
                            : "Creators added to this roster will appear here."}
                    </span>
                </span>
            </div>
        </div>
    );
}
